import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import DiagnosticCta from "@/components/DiagnosticCta";
import Seo from "@/components/Seo";
import { CASE_STUDIES } from "@/data/revamp";
import { breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

const FILTERS = ["All", "GCC", "AI-native"];

export default function CaseStudiesPage() {
  const [filter, setFilter] = useState("All");
  const shown = CASE_STUDIES.filter((c) => filter === "All" || c.segment === filter);

  return (
    <div className="bg-[var(--bg)]">
      <Seo
        title="Case Studies — GCC and AI-native HR engagements"
        description="Anonymised engagement stories from Orphion Global HR: GCC launch sprints and fractional CHRO work, each with outcome metrics. Draft case studies pending client verification."
        path="/case-studies"
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Case Studies", path: "/case-studies" },
          ]),
        ]}
      />

      <PageHero
        overline="Case studies · Proof"
        titleA="Evidence,"
        titleAccent="not adjectives."
        titleB=""
        intro="Engagement stories from GCC and AI-native work — challenge, approach, and the number that matters. Anonymised until clients approve attribution."
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
          className="mt-10 flex gap-3"
        >
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`case-filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-xs uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${
                filter === f
                  ? "border-[var(--ink)] text-[var(--ink)] bg-[var(--bg-2)]"
                  : "border-[var(--line-strong)] text-[var(--ink-70)] hover:text-[var(--ink)]"
              }`}
            >
              {f}
            </button>
          ))}
        </motion.div>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8">
          {shown.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: 0.06 * i, ease: EASE }}
            >
              <Link
                to={`/case-studies/${c.slug}`}
                className="group block border border-[var(--line-strong)] hover:bg-[var(--bg-2)] transition-colors p-8 md:p-10 h-full"
                data-testid={`case-card-${c.slug}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1">
                    {c.segment}
                  </span>
                  {c.draft && (
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-50)] border border-[var(--line-strong)] px-2 py-1">
                      Draft — pending verification
                    </span>
                  )}
                </div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)] leading-snug mb-4">
                  {c.title}
                </h2>
                <p className="font-serif-accent italic text-[var(--accent)] text-lg mb-6">{c.metric}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {c.services.map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-50)]">
                      {s}
                    </span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--ink)] group-hover:text-[var(--accent)] transition-colors">
                  Read the story
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <DiagnosticCta />
    </div>
  );
}
